'use client';

import { useEffect } from 'react';
import { trackEvent } from '@/lib/analytics';

const SCROLL_THRESHOLDS = [25, 50, 75, 90] as const;
const SECTION_VISIBILITY_THRESHOLD = 0.4;
const WHATSAPP_HOSTS = new Set(['api.whatsapp.com', 'wa.me', 'web.whatsapp.com']);

function getTrackingText(element: HTMLElement): string {
  return element.dataset.ctaText || element.textContent?.replace(/\s+/g, ' ').trim() || 'unknown';
}

function isWhatsAppLink(anchor: HTMLAnchorElement): boolean {
  try {
    return WHATSAPP_HOSTS.has(new URL(anchor.href, window.location.href).hostname.toLowerCase());
  } catch {
    return false;
  }
}

export function LandingAnalytics() {
  useEffect(() => {
    const openedFaqs = new Set<string>();
    const viewedSections = new Set<string>();
    const reachedScrollThresholds = new Set<number>();
    const handledClicks = new WeakSet<Event>();

    const handleClick = (event: MouseEvent) => {
      if (handledClicks.has(event) || !(event.target instanceof Element)) return;
      handledClicks.add(event);

      const faqTrigger = event.target.closest<HTMLElement>('[data-faq-question]');
      if (faqTrigger?.getAttribute('aria-expanded') === 'false') {
        const faqQuestion = faqTrigger.dataset.faqQuestion;
        if (faqQuestion && !openedFaqs.has(faqQuestion)) {
          openedFaqs.add(faqQuestion);
          trackEvent('faq_open', {
            faq_question: faqQuestion,
            section_name: 'faq',
          });
        }
      }

      const demoTrigger = event.target.closest<HTMLElement>('[data-demo-interest="true"]');
      if (demoTrigger) {
        trackEvent('demo_interest_click', {
          cta_location: demoTrigger.dataset.ctaLocation || 'unknown',
          cta_text: getTrackingText(demoTrigger),
        });
      }

      const pricingTrigger = event.target.closest<HTMLElement>('[data-pricing-cta="true"]');
      if (pricingTrigger) {
        trackEvent('pricing_cta_click', {
          pricing_plan: pricingTrigger.dataset.pricingPlan || 'unknown',
          cta_location: 'pricing',
          cta_text: getTrackingText(pricingTrigger),
        });
      }

      const whatsappAnchor = event.target.closest<HTMLAnchorElement>('a[href]');
      if (!whatsappAnchor || !isWhatsAppLink(whatsappAnchor)) return;

      const ctaLocation = whatsappAnchor.dataset.ctaLocation || 'unknown';
      const ctaText = getTrackingText(whatsappAnchor);
      const whatsappParams = {
        method: 'whatsapp',
        cta_location: ctaLocation,
        cta_text: ctaText,
      } as const;

      trackEvent('whatsapp_cta_click', whatsappParams);
      trackEvent('generate_lead', whatsappParams);
    };

    const sectionObserver = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (!(entry.target instanceof HTMLElement) || !entry.isIntersecting) return;

          const viewportHeight = entry.rootBounds?.height || window.innerHeight;
          const isLargeSection = entry.boundingClientRect.height > viewportHeight;
          const isVisibleEnough =
            entry.intersectionRatio >= SECTION_VISIBILITY_THRESHOLD ||
            (isLargeSection && entry.intersectionRect.height >= viewportHeight * SECTION_VISIBILITY_THRESHOLD);
          const sectionName = entry.target.dataset.section;

          if (!isVisibleEnough || !sectionName || viewedSections.has(sectionName)) return;

          viewedSections.add(sectionName);
          observer.unobserve(entry.target);
          trackEvent('section_view', { section_name: sectionName });

          if (sectionName === 'precios') {
            trackEvent('pricing_view', { section_name: 'precios' });
          }
        });
      },
      { threshold: Array.from({ length: 41 }, (_, index) => index / 100) }
    );

    document.querySelectorAll<HTMLElement>('[data-section]').forEach((section) => {
      sectionObserver.observe(section);
    });

    let scrollFrame: number | null = null;
    const measureScrollDepth = () => {
      scrollFrame = null;
      const scrollableHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollableHeight <= 0) return;

      const scrollPercent = Math.min(100, (window.scrollY / scrollableHeight) * 100);
      SCROLL_THRESHOLDS.forEach((threshold) => {
        if (scrollPercent < threshold || reachedScrollThresholds.has(threshold)) return;

        reachedScrollThresholds.add(threshold);
        trackEvent('scroll_depth', { scroll_percent: threshold });
      });
    };

    const scheduleScrollMeasurement = () => {
      if (scrollFrame !== null) return;
      scrollFrame = window.requestAnimationFrame(measureScrollDepth);
    };

    document.addEventListener('click', handleClick, true);
    window.addEventListener('scroll', scheduleScrollMeasurement, { passive: true });
    window.addEventListener('resize', scheduleScrollMeasurement);
    scheduleScrollMeasurement();

    return () => {
      document.removeEventListener('click', handleClick, true);
      window.removeEventListener('scroll', scheduleScrollMeasurement);
      window.removeEventListener('resize', scheduleScrollMeasurement);
      sectionObserver.disconnect();
      if (scrollFrame !== null) window.cancelAnimationFrame(scrollFrame);
    };
  }, []);

  return <span hidden aria-hidden="true" data-landing-analytics="active" />;
}
